import { useRef, useState, useEffect } from 'react';
import { MdCheckCircle, MdRefresh, MdArrowBack } from 'react-icons/md';
import SignatureCanvas from 'react-signature-canvas';
import { getTotalHT, getTotalTTC, isCustomerHT } from 'views/orders/createOrders/utils';
import { computeOrderTotals } from 'interfaces/order';

export interface FinalizationOptions {
    shouldInvoice: boolean;
    registerPayment: boolean;
    paymentAmount: number;
    paymentMethod: 'cash' | 'check' | 'transfer' | 'credit-card' | 'direct-debit';
    paymentReference: string;
    sendInvoiceByEmail: boolean;
    emailSubject: string;
    emailContent: string;
}

interface SignatureStepProps {
    orderId: string | null;
    customer: any;
    orderItems: any[];
    sellerCompany?: string;
    onSignatureComplete: (signature: string, options: FinalizationOptions) => void;
    onSkip: (options: FinalizationOptions) => void;
    onBack: () => void;
    isProcessing: boolean;
}

const SignatureStep = ({ orderId, customer, orderItems, sellerCompany, onSignatureComplete, onSkip, onBack, isProcessing }: SignatureStepProps) => {
    const signatureRef = useRef<any>(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Fallback client-side (affiché pendant le chargement)
    const fallbackIsCustomerHT = isCustomerHT(customer);
    const fallbackTotalHT = getTotalHT(orderItems || []);
    const fallbackTotalTTC = getTotalTTC(orderItems || [], { isCustomerHT: fallbackIsCustomerHT });

    const [totalHT, setTotalHT] = useState<number>(fallbackTotalHT);
    const [totalTTC, setTotalTTC] = useState<number>(fallbackTotalTTC);
    const [customerIsHT, setCustomerIsHT] = useState<boolean>(fallbackIsCustomerHT);
    const [totalsLoading, setTotalsLoading] = useState(true);

    const [shouldInvoice, setShouldInvoice] = useState(false);
    const [registerPayment, setRegisterPayment] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState<number>(Number(fallbackTotalTTC.toFixed(2)));
    const [paymentMethod, setPaymentMethod] = useState<
        'cash' | 'check' | 'transfer' | 'credit-card' | 'direct-debit'
    >('credit-card');
    const [paymentReference, setPaymentReference] = useState<string>('');
    const [sendInvoiceByEmail, setSendInvoiceByEmail] = useState(false);
    const [emailSubject, setEmailSubject] = useState(`Votre facture ${sellerCompany}`);
    const [emailContent, setEmailContent] = useState(
        `Bonjour,\n\nVeuillez trouver ci-joint votre facture ${sellerCompany}.\n\nCordialement`
    );

    // Fetch confirmed totals from backend (single source of truth)
    useEffect(() => {
        if (!orderItems || orderItems.length === 0) {
            setTotalsLoading(false);
            return;
        }
        let cancelled = false;
        setTotalsLoading(true);
        computeOrderTotals(orderItems, customer?._id).then((result) => {
            if (cancelled) return;
            if (result) {
                setTotalHT(result.totalHT);
                setTotalTTC(result.totalTTC);
                setCustomerIsHT(result.isCustomerHT);
                setPaymentAmount(Number(result.totalTTC.toFixed(2)));
            }
            setTotalsLoading(false);
        });
        return () => { cancelled = true; };
    }, [orderItems, customer?._id]);

    useEffect(() => {
        // Initialize canvas background as white
        if (signatureRef.current) {
            const canvas = signatureRef.current.getCanvas();
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
    }, []);

    const handleClear = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            // Redraw white background
            const canvas = signatureRef.current.getCanvas();
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            setIsEmpty(true);
        }
    };

    const handleEnd = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            setIsEmpty(false);
        }
    };

    const handleConfirm = () => {
        if (hasSubmitted || isProcessing) return;
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            setHasSubmitted(true);
            const signatureData = signatureRef.current.toDataURL('image/png');
            onSignatureComplete(signatureData, {
                shouldInvoice,
                registerPayment,
                paymentAmount,
                paymentMethod,
                paymentReference,
                sendInvoiceByEmail,
                emailSubject,
                emailContent,
            });
        }
    };

    const handleSkip = () => {
        if (hasSubmitted || isProcessing) return;
        setHasSubmitted(true);
        onSkip({
            shouldInvoice,
            registerPayment,
            paymentAmount,
            paymentMethod,
            paymentReference,
            sendInvoiceByEmail,
            emailSubject,
            emailContent,
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <button
                    onClick={onBack}
                    disabled={isProcessing || hasSubmitted}
                    className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 transition duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                    <MdArrowBack className="h-4 w-4" />
                    Retour aux produits
                </button>
            </div>

            <div className="rounded-lg bg-blue-50 p-4 dark:bg-navy-900">
                <h3 className="mb-2 font-bold text-navy-700 dark:text-white">
                    Récapitulatif de la commande
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p><strong>Client:</strong> {customer.company || customer.displayName}</p>
                    <p><strong>Nombre d'articles:</strong> {orderItems.length}</p>
                    <p>
                        <strong>Montant total HT:</strong>{' '}
                        {totalsLoading ? <span className="animate-pulse text-gray-400">calcul…</span> : `${totalHT.toFixed(2)}€`}
                    </p>
                    <p>
                        <strong>Montant total TTC:</strong>{' '}
                        {totalsLoading
                            ? <span className="animate-pulse text-gray-400">calcul…</span>
                            : `${totalTTC.toFixed(2)}€${customerIsHT ? ' (client HT)' : ''}`}
                    </p>
                </div>
            </div>

            <div className="rounded-lg border-2 border-gray-300 p-4 dark:border-gray-600">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-bold text-navy-700 dark:text-white">
                        Signature du client
                    </h3>
                    <button
                        onClick={handleClear}
                        disabled={isProcessing || isEmpty}
                        className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1 text-sm text-gray-700 transition duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                    >
                        <MdRefresh className="h-4 w-4" />
                        Effacer
                    </button>
                </div>
                
                <div className="rounded-lg border border-gray-300 bg-white dark:border-gray-600">
                    <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                            className: 'signature-canvas w-full h-64 touch-none',
                            style: { touchAction: 'none' }
                        }}
                        backgroundColor="white"
                        penColor="black"
                        onEnd={handleEnd}
                    />
                </div>
                
                <p className="mt-2 text-xs text-gray-500">
                    Dessinez la signature du client dans le cadre ci-dessus
                </p>
            </div>

            <div className="rounded-lg border-2 border-gray-300 p-4 dark:border-gray-600">
                <h3 className="mb-3 font-bold text-navy-700 dark:text-white">
                    Paiement et envoi de facture
                </h3>

                <div className="mb-3 flex items-center gap-2">
                    <input
                        id="should-invoice"
                        type="checkbox"
                        checked={shouldInvoice}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setShouldInvoice(checked);
                            if (!checked) {
                                setRegisterPayment(false);
                                setSendInvoiceByEmail(false);
                            }
                        }}
                        disabled={isProcessing}
                    />
                    <label htmlFor="should-invoice" className="text-sm text-gray-700 dark:text-gray-300">
                        Facturer cette commande
                    </label>
                </div>

                <div className="mb-3 flex items-center gap-2">
                    <input
                        id="register-payment"
                        type="checkbox"
                        checked={registerPayment}
                        onChange={(e) => setRegisterPayment(e.target.checked)}
                        disabled={isProcessing || !shouldInvoice}
                    />
                    <label htmlFor="register-payment" className="text-sm text-gray-700 dark:text-gray-300">
                        Enregistrer un règlement maintenant
                    </label>
                </div>

                {shouldInvoice && registerPayment && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                            <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Montant réglé</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                                disabled={isProcessing}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Mode de paiement</label>
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value as 'credit-card' | 'cash' | 'check' | 'transfer' | 'direct-debit')}
                                disabled={isProcessing}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                            >
                                <option value="credit-card">Carte bancaire</option>
                                <option value="cash">Espèces</option>
                                <option value="transfer">Virement</option>
                                <option value="direct-debit">Prélèvement</option>
                                <option value="check">Chèque</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Référence</label>
                            <input
                                type="text"
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                disabled={isProcessing}
                                placeholder="Ticket / Réf."
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                            />
                        </div>
                    </div>
                )}

                <div className="mb-3 mt-4 flex items-center gap-2">
                    <input
                        id="send-invoice"
                        type="checkbox"
                        checked={sendInvoiceByEmail}
                        onChange={(e) => setSendInvoiceByEmail(e.target.checked)}
                        disabled={isProcessing || !shouldInvoice}
                    />
                    <label htmlFor="send-invoice" className="text-sm text-gray-700 dark:text-gray-300">
                        Envoyer la facture par email depuis cette modale
                    </label>
                </div>

                {shouldInvoice && sendInvoiceByEmail && (
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Objet</label>
                            <input
                                type="text"
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                disabled={isProcessing}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Contenu email</label>
                            <textarea
                                value={emailContent}
                                onChange={(e) => setEmailContent(e.target.value)}
                                disabled={isProcessing}
                                rows={4}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                            />
                        </div>
                        <p className="text-xs text-gray-500">
                            Email destinataire: {customer?.billingEmail || customer?.secondBillingEmail || customer?.mail || 'non renseigné'}
                        </p>
                    </div>
                )}
            </div>

            <div className="mt-4 flex justify-between gap-3">
                <button
                    onClick={handleSkip}
                    disabled={isProcessing || hasSubmitted}
                    className="rounded-xl border-2 border-gray-300 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                >
                    Ignorer la signature
                </button>
                <button
                    onClick={handleConfirm}
                    disabled={isEmpty || isProcessing || hasSubmitted}
                    className="flex items-center gap-2 rounded-xl bg-toola-500 px-6 py-3 text-base font-medium text-white transition duration-200 hover:bg-toola-600 active:bg-toola-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isProcessing ? (
                        <>
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Finalisation...
                        </>
                    ) : (
                        <>
                            <MdCheckCircle className="h-5 w-5" />
                            Valider et finaliser
                        </>
                    )}
                </button>
            </div>

            <div className="rounded-lg bg-green-50 p-3 text-sm text-gray-600 dark:bg-navy-900 dark:text-gray-400">
                <p>
                    ✓ La commande sera automatiquement marquée comme livrée<br />
                    {shouldInvoice && <>✓ Une facture sera générée{sendInvoiceByEmail ? ' et envoyée par email' : ''}<br /></>}
                    ✓ Le bon de livraison sera signé électroniquement
                </p>
            </div>
        </div>
    );
};

export default SignatureStep;
